import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  Chip,
  CircularProgress,
  IconButton,
  alpha,
  Tooltip,
} from '@mui/material';
import { Close as CloseIcon, LockOutlined as LockIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import {
  getSystemTools,
  getOrganisationTools,
  getProjectTools,
  enableProjectTool,
  disableProjectTool,
  SystemTool,
  OrganisationTool,
  ProjectTool,
} from 'src/api2/toolsApi';
import { useSnackbar } from 'src/view/components/SnackbarService';
import RequestToolAccessModal from '../../components/RequestToolAccessModal';

interface ProjectToolsDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  orgId?: string;
  projectName?: string;
}

export const ProjectToolsDialog: React.FC<ProjectToolsDialogProps> = ({
  open,
  onClose,
  projectId,
  orgId,
  projectName = 'Project',
}) => {
  const { showSnackbar, snackbarType } = useSnackbar();
  const [systemTools, setSystemTools] = useState<SystemTool[]>([]);
  const [orgTools, setOrgTools] = useState<OrganisationTool[]>([]);
  const [projectTools, setProjectTools] = useState<ProjectTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingToolId, setTogglingToolId] = useState<string | null>(null);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestToolName, setRequestToolName] = useState('');

  const loadData = useCallback(async () => {
    if (!open || !projectId) return;
    setLoading(true);
    try {
      const [sysRes, projRes, orgRes] = await Promise.all([
        getSystemTools().catch(() => []),
        getProjectTools(projectId).catch(() => []),
        orgId ? getOrganisationTools(orgId).catch(() => []) : Promise.resolve([]),
      ]);
      setSystemTools(sysRes);
      setProjectTools(projRes);
      setOrgTools(orgRes);
    } catch (err) {
      console.error('Failed to load project tools data:', err);
    } finally {
      setLoading(false);
    }
  }, [open, projectId, orgId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fallbackCatalog: SystemTool[] = [
    { tool_id: 'bioacoustic', slug: 'bioacoustic', name: 'Bioacoustics', tool_name: 'bioacoustic', display_name: 'Bioacoustics', description: 'Detect and classify wildlife sounds and bird species.', is_active: true },
    { tool_id: 'flora_intelligence', slug: 'flora_intelligence', name: 'Flora Intelligence', tool_name: 'flora_intelligence', display_name: 'Flora Intelligence', description: 'Identify plant species and assess vegetation health.', is_active: true },
    { tool_id: 'species_vision', slug: 'species_vision', name: 'Species Vision', tool_name: 'species_vision', display_name: 'Species Vision', description: 'AI models to identify species using computer vision.', is_active: true },
    { tool_id: 'nature_metrics', slug: 'nature_metrics', name: 'Nature Metrics', tool_name: 'nature_metrics', display_name: 'Nature Metrics', description: 'Quantify biodiversity, habitat health and ecosystem integrity.', is_active: true },
    { tool_id: 'tnfd_analytics', slug: 'tnfd_analytics', name: 'TNFD Analytics', tool_name: 'tnfd_analytics', display_name: 'TNFD Analytics', description: 'Generate TNFD aligned reports and disclosures.', is_active: true },
    { tool_id: 'climate_intelligence', slug: 'climate_intelligence', name: 'Climate Intelligence', tool_name: 'climate_intelligence', display_name: 'Climate Intelligence', description: 'Assess climate risks, vulnerability and adaptation strategies.', is_active: true },
  ];

  const toolsList = systemTools.length > 0 ? systemTools : fallbackCatalog;

  const handleToggle = async (tool: SystemTool) => {
    const tSlug = tool.slug || tool.tool_name || tool.tool_id;
    const tName = tool.name || tool.display_name || 'Tool';
    const existingProjectTool = projectTools.find(
      (pt) => pt.tool_id === tool.tool_id || pt.tool?.slug === tSlug || pt.tool?.tool_name === tSlug
    );

    setTogglingToolId(tool.tool_id);
    try {
      if (existingProjectTool) {
        await disableProjectTool(projectId, existingProjectTool.project_tool_id);
        showSnackbar({ message: `${tName} disabled for project.`, type: snackbarType.SUCCESS });
      } else {
        await enableProjectTool(projectId, tool.tool_id);
        showSnackbar({ message: `${tName} enabled for project.`, type: snackbarType.SUCCESS });
      }
      const updatedProjectTools = await getProjectTools(projectId);
      setProjectTools(updatedProjectTools);
    } catch (err: any) {
      let rawMsg = err?.detail?.[0]?.msg || err?.detail || err?.error?.message || err?.message || 'Failed to update tool state';
      if (typeof rawMsg !== 'string') {
        rawMsg = JSON.stringify(rawMsg);
      }

      let userMsg = rawMsg;
      if (rawMsg.toLowerCase().includes('organisation does not have') || rawMsg.toLowerCase().includes('entitlement')) {
        userMsg = `Your organisation does not have active access to "${tName}". Please contact your organisation administrator to grant access.`;
      }

      showSnackbar({ message: userMsg, type: snackbarType.ERROR });
    } finally {
      setTogglingToolId(null);
    }
  };

  const handleOpenRequestModal = (toolName: string) => {
    setRequestToolName(toolName);
    setRequestModalOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#0F0F0F',
            border: '1px solid #222',
            borderRadius: '14px',
            color: '#fff',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid #1F1F1F',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: '#fff' }}>
              Project Tools Management — {projectName}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: '#777', mt: 0.25 }}>
              Enable tools for this project. Only tools active in your Organisation can be enabled.
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#777', '&:hover': { color: '#fff' } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress sx={{ color: '#10B981' }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {toolsList.map((tool) => {
                const tSlug = (tool.slug || tool.tool_name || tool.tool_id || '').toLowerCase();
                const tName = tool.name || tool.display_name || 'Tool';
                const tId = String(tool.tool_id || '').toLowerCase();

                const orgTool = orgId
                  ? orgTools.find((ot) => {
                      if (!ot) return false;
                      const otId = String(ot.tool_id || '').toLowerCase();
                      const otOrgToolId = String(ot.org_tool_id || '').toLowerCase();
                      if (otId && tId && otId === tId) return true;
                      if (otOrgToolId && tId && otOrgToolId === tId) return true;

                      if (ot.tool) {
                          const nestedId = String(ot.tool.tool_id || '').toLowerCase();
                          const nestedSlug = (ot.tool.slug || ot.tool.tool_name || ot.tool.tool_id || '').toLowerCase();
                          const nestedName = (ot.tool.name || ot.tool.display_name || '').toLowerCase();
                          if (nestedId && tId && nestedId === tId) return true;
                          if (nestedSlug && tSlug && (nestedSlug === tSlug || nestedSlug.replace(/_/g, '-') === tSlug.replace(/_/g, '-'))) return true;
                          if (nestedName && tName && nestedName.toLowerCase() === tName.toLowerCase()) return true;
                      }

                      if (otId && tSlug && (otId === tSlug || otId.replace(/_/g, '-') === tSlug.replace(/_/g, '-'))) return true;
                      return false;
                    })
                  : null;

                const isOrgActive = !orgId || (orgTool && orgTool.status === 'active');

                const projectTool = projectTools.find((pt) => {
                  if (!pt) return false;
                  const ptId = String(pt.tool_id || '').toLowerCase();
                  const ptProjectToolId = String(pt.project_tool_id || '').toLowerCase();
                  if (ptId && tId && ptId === tId) return true;
                  if (ptProjectToolId && tId && ptProjectToolId === tId) return true;

                  if (pt.tool) {
                      const nestedId = String(pt.tool.tool_id || '').toLowerCase();
                      const nestedSlug = (pt.tool.slug || pt.tool.tool_name || pt.tool.tool_id || '').toLowerCase();
                      const nestedName = (pt.tool.name || pt.tool.display_name || '').toLowerCase();
                      if (nestedId && tId && nestedId === tId) return true;
                      if (nestedSlug && tSlug && (nestedSlug === tSlug || nestedSlug.replace(/_/g, '-') === tSlug.replace(/_/g, '-'))) return true;
                      if (nestedName && tName && nestedName.toLowerCase() === tName.toLowerCase()) return true;
                  }

                  if (ptId && tSlug && (ptId === tSlug || ptId.replace(/_/g, '-') === tSlug.replace(/_/g, '-'))) return true;
                  return false;
                });

                const isProjectEnabled = !!projectTool;
                const isBusy = togglingToolId === tool.tool_id;

                return (
                  <Box
                    key={tool.tool_id}
                    sx={{
                      p: 2,
                      borderRadius: '10px',
                      border: `1px solid ${
                        isProjectEnabled
                          ? alpha('#10B981', 0.4)
                          : isOrgActive
                          ? 'rgba(255,255,255,0.08)'
                          : 'rgba(255,255,255,0.03)'
                      }`,
                      bgcolor: isProjectEnabled
                        ? 'rgba(16,185,129,0.05)'
                        : 'rgba(255,255,255,0.02)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      opacity: isOrgActive ? 1 : 0.65,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#fff' }}>
                          {tName}
                        </Typography>
                        {isProjectEnabled && (
                          <Chip
                            label="Project Enabled"
                            size="small"
                            icon={<CheckCircleIcon sx={{ fontSize: '12px !important', color: '#10B981' }} />}
                            sx={{
                              height: 20,
                              bgcolor: 'rgba(16,185,129,0.15)',
                              color: '#10B981',
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              border: '1px solid rgba(16,185,129,0.3)',
                            }}
                          />
                        )}
                        {!isOrgActive && (
                          <Chip
                            label="Not Enabled for Organisation"
                            size="small"
                            icon={<LockIcon sx={{ fontSize: '12px !important', color: '#F59E0B' }} />}
                            sx={{
                              height: 20,
                              bgcolor: 'rgba(245,158,11,0.1)',
                              color: '#F59E0B',
                              fontSize: '0.62rem',
                              fontWeight: 800,
                              border: '1px solid rgba(245,158,11,0.3)',
                            }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', color: '#888' }}>
                        {tool.description || 'Environmental intelligence module'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isOrgActive ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isBusy && <CircularProgress size={16} sx={{ color: '#10B981' }} />}
                          <Switch
                            checked={isProjectEnabled}
                            disabled={isBusy}
                            onChange={() => handleToggle(tool)}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#10B981',
                                '&:hover': { backgroundColor: 'rgba(16, 185, 129, 0.08)' },
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#10B981',
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Tooltip title="Organisation must grant this tool before a project can enable it. Click to request tool access.">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenRequestModal(tName)}
                            sx={{
                              color: '#F59E0B',
                              borderColor: 'rgba(245,158,11,0.4)',
                              borderRadius: '7px',
                              textTransform: 'none',
                              fontSize: '0.72rem',
                              fontWeight: 800,
                              '&:hover': {
                                borderColor: '#F59E0B',
                                bgcolor: 'rgba(245,158,11,0.08)',
                              },
                            }}
                          >
                            Enable Tool (Request Access)
                          </Button>
                        </Tooltip>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #1F1F1F' }}>
          <Button onClick={onClose} sx={{ color: '#999', fontWeight: 800 }}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <RequestToolAccessModal
        open={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        toolName={requestToolName}
      />
    </>
  );
};

export default ProjectToolsDialog;
